import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { baseInputClass, isSupportedType } from './constants';
import {
  AcknowledgeInput,
  AudioInput,
  BarcodeInput,
  BeginRepeatInput,
  FileInput,
  HeaderInput,
  HyperlinkInput,
  ImageInput,
  InformationNote,
  LikertInput,
  RangeSliderInput,
  RankingInput,
  RatingInput,
  SignatureInput,
  TemplateIntro,
  TriggerInput,
  VideoInput,
  YesNoInput,
} from './inputs';
import DateInput from './inputs/DateInput';
import DateMonthYearInput from './inputs/DateMonthYearInput';
import DateTimeInput from './inputs/DateTime';
import DateYearInput from './inputs/DateYearInput';
import DropdownMinimal from './inputs/DropdownMinimal';
import GeopointInput from './inputs/GeoPointInput';
import { PasswordInput } from './inputs/MaskedTextInput';
import { NumberQuestionInput } from './inputs/NumberInput';
import RankingTypeInput from './inputs/RankingTyeInput';
import SelectHorizontalColumns from './inputs/SelectHorizontalColumns';
import { TextGroupInput } from './inputs/TextGroup';
import TimeInput from './inputs/Time';
import { GeoPoint, QuestionRendererProps, SurveyQuestion } from './types';

// Re-export types
export type { GeoPoint, QuestionRendererProps, SurveyQuestion } from './types';

/** Question Group (collapsible) */
const QuestionGroup = ({
  question,
  renderQuestion,
  getLabel,
  index,
}: {
  question: SurveyQuestion;
  renderQuestion: (q: SurveyQuestion, i: number) => React.ReactElement;
  getLabel: (obj: Record<string, any>) => string;
  index: number;
}) => {
  const [open, setOpen] = useState(true);
  const label = getLabel(question);

  return (
    <div className="border border-[#C0275E] rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#C0275E] text-white text-sm font-semibold"
      >
        <span>{label || 'Question Group'}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="divide-y divide-pink-100 px-4">
          {question.questions?.map((q, i) => {
            const childLabel = getLabel(q);

            return (
              <div key={i} className="py-4 border-b last:border-b-0">
                {childLabel && <h3 className="font-semibold text-[#C0275E] mb-1">{childLabel}</h3>}

                {q.required === 'yes' && (
                  <span className="text-xs text-red-500 block mb-1">* Required</span>
                )}

                {renderQuestion(q, i)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const QuestionRenderer = ({
  questions,
  disabled = true,
  className = '',
  selectedLanguage,
}: QuestionRendererProps) => {
  const [currentLocation, setCurrentLocation] = useState<GeoPoint | null>(null);

  const getLabel = useCallback(
    (obj: Record<string, any>): string => {
      if (!obj) return '';
      if (selectedLanguage && obj[`label::${selectedLanguage}`])
        return obj[`label::${selectedLanguage}`];
      const fallbackKey = Object.keys(obj).find((k) => k.startsWith('label::'));
      return fallbackKey ? obj[fallbackKey] : '';
    },
    [selectedLanguage],
  );

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => setCurrentLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setCurrentLocation({ lat: 40.7128, lng: -74.006 }),
    );
  }, []);

  const renderQuestionInput = (
    question: SurveyQuestion,
    index: number,
    parentIndex?: number,
  ): React.ReactElement => {
    const type = question.type?.toLowerCase?.() ?? '';
    const name = question?.name?.toLowerCase?.() ?? '';

    const label = getLabel(question);

    // Calculate display number: if inside repeat, use parent.child format (e.g., 35.1)
    const displayNumber = parentIndex !== undefined ? `${parentIndex}.${index + 1}` : index + 1;

    // ── Existing types ──────────────────────────────────────────────────────
    if (type.startsWith('text')) {
      if (name === 'short_text')
        return <input type="text" className={baseInputClass} disabled={disabled} />;
      if (name === 'long_text') return <textarea className={baseInputClass} disabled={disabled} />;
      if (name === 'masked_text') return <PasswordInput disabled={disabled} />;
      if (name === 'number_text')
        return <input type="text" className={baseInputClass} disabled={disabled} />;
    }
    // ── Existing types ──────────────────────────────────────────────────────
    if (type.startsWith('note')) {
      if (name === 'integer')
        return <input type="number" className={baseInputClass} disabled={disabled} />;
      if (name === 'long_text') return <textarea className={baseInputClass} disabled={disabled} />;
      if (name === 'masked_text') return <PasswordInput disabled={disabled} />;
      if (name === 'number_text')
        return <input type="text" className={baseInputClass} disabled={disabled} />;
    }
    if (type === 'long_text' || type === 'textarea')
      return <input type="textarea" className={baseInputClass} disabled={disabled} />;

    if (type === 'integer' || type === 'decimal' || type === 'integer')
      return <NumberQuestionInput type={name} disabled={disabled} />;

    if (type === 'decimal')
      return <input type="number" className={baseInputClass} disabled={disabled} />;

    if (type.startsWith('date')) {
      if (name === 'date_q') return <DateInput disabled={disabled} className={baseInputClass} />;
      if (name === 'date_month_year')
        return <DateMonthYearInput disabled={disabled} className={baseInputClass} />;
      if (name === 'date_year')
        return <DateYearInput disabled={disabled} className={baseInputClass} />;
    }

    if (type === 'time')
      // return <div><input type="time" className={baseInputClass} disabled={disabled} /> </div>

      return <TimeInput disabled={disabled} className={baseInputClass} />;

    if (type === 'datetime')
      // return <input type="datetime-local" className={baseInputClass} disabled={disabled} />;
      return <DateTimeInput disabled={disabled} className={baseInputClass} />;
    // if (type === 'note') return <textarea className={baseInputClass} rows={3} disabled={disabled} />;

    if (type === 'geopoint') {
      return (
        // <div className="space-y-2">
        //   <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        //     <MapPin size={18} className="text-blue-600" />
        //     <div className="text-sm text-blue-800">
        //       <p className="font-medium">GPS Location will be captured</p>
        //       <p className="text-xs">
        //         {currentLocation
        //           ? `Current Location: ${currentLocation.lat.toFixed(
        //             4,
        //           )}, ${currentLocation.lng.toFixed(4)}`
        //           : 'Please allow location access'}
        //       </p>
        //     </div>
        //   </div>
        //   {currentLocation && (
        //     <div className="h-48 border border-[#C0275E] rounded-lg overflow-hidden">
        //       <iframe
        //         src={`https://www.openstreetmap.org/export/embed.html?bbox=${currentLocation.lng - 0.01
        //           },${currentLocation.lat - 0.01},${currentLocation.lng + 0.01},${currentLocation.lat + 0.01
        //           }&layer=mapnik&marker=${currentLocation.lat},${currentLocation.lng}`}
        //         width="100%"
        //         height="100%"
        //         style={{ border: 0 }}
        //         title="OpenStreetMap Preview"
        //       />
        //     </div>
        //   )}
        // </div>
        <GeopointInput disabled={disabled} className={baseInputClass} />
      );
    }

    // Check for likert_opts before generic select_one (e.g., "select_one likert_opts")
    if (
      type.includes('likert_opts') ||
      type === 'likert' ||
      type === 'likert_q' ||
      type === 'likert_scale'
    ) {
      return <LikertInput disabled={disabled} />;
    }

    if (type.startsWith('select_one')) {
      if (name === 'select_minimal') {
        let options = question.options ?? [];
        return (
          <DropdownMinimal
            disabled={disabled}
            className={baseInputClass}
            options={options}
            getLabel={getLabel}
          />
        );
      }
      return (
        <div className="ml-3 space-y-2">
          {question.options?.map((opt, i) => (
            <label key={i} className="flex items-center gap-2 text-sm text-[#621C43]">
              <input
                type="radio"
                name={`question_${displayNumber}`}
                disabled={disabled}
                className="accent-[#C0275E]"
              />
              <span>{getLabel(opt)}</span>
            </label>
          ))}
        </div>
      );
    }

    if (type.startsWith('select_multiple')) {
      if (name === 'select_columns') {
        let options = question.options ?? [];

        return (
          <SelectHorizontalColumns
            disabled={disabled}
            className={baseInputClass}
            options={options}
            getLabel={getLabel}
          />
        );
      }
      return (
        <div className="ml-3 space-y-2">
          {question.options?.map((opt, i) => (
            <label key={i} className="flex items-center gap-2 text-sm text-[#621C43]">
              <input type="checkbox" disabled={disabled} className="accent-[#C0275E]" />
              <span>{getLabel(opt)}</span>
            </label>
          ))}
        </div>
      );
    }

    // ── New types ───────────────────────────────────────────────────────────
    if (type === 'audio') return <AudioInput disabled={disabled} />;

    if (type === 'video') return <VideoInput disabled={disabled} />;

    if (type === 'image') {
      return <ImageInput disabled={disabled} />;
    }

    if (type === 'file') return <FileInput disabled={disabled} />;

    if (type === 'rating') return <RatingInput disabled={disabled} label={label} />;

    if (name === 'ranking_q' && type === 'rank ranking_items') {
      // let options = [
      //   {
      //     name: '1',
      //     list_name: 'multi_opts',
      //     'label::English (en)': 'Option 1',
      //     'label::Español (es)': 'Opción 1',
      //     'label::हिन्दी (hi)': 'विकल्प 1',
      //   },
      //   {
      //     name: '2',
      //     list_name: 'multi_opts',
      //     'label::English (en)': 'Option 2',
      //     'label::Español (es)': 'Opción 2',
      //     'label::हिन्दी (hi)': 'विकल्प 2',
      //   },
      //   {
      //     name: '3',
      //     list_name: 'multi_opts',
      //     'label::English (en)': 'Option 3',
      //     'label::Español (es)': 'Opción 3',
      //     'label::हिन्दी (hi)': 'विकल्प 3',
      //   },
      //   {
      //     name: '4',
      //     list_name: 'multi_opts',
      //     'label::English (en)': 'Option 4',
      //     'label::Español (es)': 'Opción 4',
      //     'label::हिन्दी (hi)': 'विकल्प 4',
      //   },
      //   {
      //     name: '4',
      //     list_name: 'multi_opts',
      //     'label::English (en)': 'Option 5',
      //     'label::Español (es)': 'Opción 5',
      //     'label::हिन्दी (hi)': 'विकल्प 5',
      //   },
      // ];
      let options = question.options ?? [];
      return (
        <RankingTypeInput
          options={options}
          className={baseInputClass}
          getLabel={getLabel}
          disabled={disabled}
        />
      );
    }

    if (type === 'yes_no' || type === 'yesno' || type === 'select_one')
      return <YesNoInput disabled={disabled} />;

    if (type === 'info' || type === 'information_note' || type === 'informational')
      return <InformationNote hint={question.hint} />;

    if (type === 'barcode') {
      return <BarcodeInput disabled={disabled} />;
    }

    if (type === 'text' && name === 'hyperlink_q') {
      return <HyperlinkInput disabled={disabled} label={question?.default} />;
    }

    if (type === 'signature') return <SignatureInput disabled={disabled} />;

    if (type === 'trigger') return <TriggerInput disabled={disabled} label={label} />;

    if (type === 'begin_group' || type === 'group') {
      return (
        <QuestionGroup
          question={question}
          getLabel={getLabel}
          renderQuestion={renderQuestionInput}
          index={index}
        />
      );
    }

    if (type === 'geotrace')
      return <GeopointInput disabled={disabled} className={baseInputClass} />;

    if (type === 'geoshape')
      return <GeopointInput disabled={disabled} className={baseInputClass} />;

    if (type === 'template_intro' || type === 'templateintro')
      return <TemplateIntro disabled={disabled} description={question.hint} />;

    // ── Additional new types ────────────────────────────────────────────────
    if (type === 'header') return <HeaderInput label={label} />;

    if (type === 'range_slider') return <RangeSliderInput disabled={disabled} label={label} />;
    if (type === 'range') return <RangeSliderInput disabled={disabled} label={label} />;

    if (type === 'acknowledge') {
      return <AcknowledgeInput disabled={disabled} label={label} />;
    }

    if (type === 'text' && (name === 'grp_text' || name === 'member_name')) {
      return <TextGroupInput disabled={disabled} className={className} />;
    }
    if (type === 'begin_repeat') {
      return (
        <BeginRepeatInput
          label={label}
          questions={question.questions ?? []}
          questionIndex={index + 1}
          getLabel={getLabel}
          renderQuestion={renderQuestionInput}
          disabled={disabled}
        />
      );
    }

    if (type === 'end_group') return <></>;

    if (type === 'rank ranking_items' || type === 'rank_ranking_items') {
      return (
        <RankingInput options={question.options ?? []} getLabel={getLabel} disabled={disabled} />
      );
    }

    // Unsupported type - don't render anything
    return <></>;
  };

  if (!questions?.length) {
    return <p className="text-sm text-gray-500">No questions available</p>;
  }

  return (
    <div className={`${className} relative`}>
      <div className="sticky top-0 right-0 flex justify-end pointer-events-none z-10">
        <span className="text-xs text-[#C0275E] px-2 py-1">Powered by Bluenumber®</span>
      </div>
      {questions.map((question, index) => {
        const type = question.type?.toLowerCase?.() ?? '';
        const isGroup = type === 'begin_group' || type === 'group';
        const isRepeat = type === 'begin_repeat';
        const isUnsupported = !isSupportedType(type);

        // For groups, repeats, and unsupported types, render without the outer label wrapper
        // (they handle their own labels internally or shouldn't show labels)
        if (isGroup || isRepeat || isUnsupported) {
          return (
            <div key={index} className="py-6 border-b last:border-b-0">
              {renderQuestionInput(question, index)}
            </div>
          );
        }

        const questionLabel = getLabel(question);
        const shouldShowLabel = questionLabel && !questionLabel.startsWith('──');

        // If label starts with ──, render without the wrapper div to avoid empty space
        if (
          !shouldShowLabel &&
          !question.hint &&
          question.required !== 'yes' &&
          !question.constraint_message
        ) {
          return (
            <React.Fragment key={index}>{renderQuestionInput(question, index)}</React.Fragment>
          );
        }

        return (
          <div key={index} className="py-4 border-b last:border-b-0">
            {shouldShowLabel && (
              <h3 className="font-semibold text-[#C0275E] mb-1">{questionLabel}</h3>
            )}

            {question.hint && question.type !== 'info' && (
              <p className="text-sm text-[#C0275E] mb-2 italic">{question.hint}</p>
            )}

            {question.required === 'yes' && (
              <span className="text-xs text-red-500 block mb-1">* Required</span>
            )}

            {renderQuestionInput(question, index)}

            {question.constraint_message && (
              <p className="text-xs text-gray-500 mt-1 italic">
                Constraint: {question.constraint_message}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default QuestionRenderer;
